import React from "react";
import classNames from "classnames";

import {
  useAuthentication,
  useEditAllocation,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
} from "@planzone/view-models";
import {
  usePlanzonePermissions,
  useProjectPermissions,
} from "@components/permissions";
import { allocation } from "@planzone/dto";
import { NumberInput } from "../record/numberInput";
import { toFixed } from "@pages/sharedTimeSheetUtils";
import { Props } from "./types";
import styles from "./styles.scss";

function ResourceTimeOverview({
  type,
  spentTime,
  remainingTime,
  spentTimeInWeek,
  allocatedHours,
  progression,
  task,
  project,
  resource,
  showCompletionColumn,
  resourceAllocation,
}: Props) {
  const [nextAllocatedHours, setNextAllocatedHours] = React.useState<number>(
    +toFixed(progression ?? 0),
  );
  const [nextAllocated, setNextAllocated] = React.useState<number>(
    +toFixed(allocatedHours),
  );
  React.useEffect(() => {
    setNextAllocated(+toFixed(allocatedHours));
  }, [allocatedHours]);
  const { editAllocation } = useEditAllocation({
    ...allocation,
    uuid: task?.allocations[0]?.uuid ?? "",
  });
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { account } = useAuthentication();
  const { isAdministrator } = usePlanzonePermissions();
  const { isProjectManager } = useProjectPermissions(project);
  const canEdit =
    isAdministrator ||
    (resource.uuid === account.resource.uuid &&
      settings.canEditPersonalTimeSheet) ||
    (isProjectManager && settings.canEditProjectPersonalTimeSheet) ||
    settings.canEditManagerPersonalTimeSheet;

  return (
    <div
      className={classNames(styles.resourceTimeOverview, {
        [styles.resource]: type === "resource",
        [styles.list]: type === "list" || type === "project",
        [styles.task]: type === "task",
      })}
    >
      <div className={styles.cell}>
        {type === "task" && canEdit && resourceAllocation ? (
          <div className={styles.numberInput}>
            <NumberInput
              nbr={nextAllocated}
              max={Infinity}
              edit={(nbr) => nbr >= 0 && setNextAllocated(nbr)}
              submit={(hours) =>
                hours !== +toFixed(allocatedHours) &&
                editAllocation({
                  allocation: { ...allocation, uuid: resourceAllocation.uuid },
                  nextAllocation: {
                    ...allocation,
                    resource,
                    allocatedHours: hours,
                    percentComplete: resourceAllocation.percentComplete,
                  },
                })
              }
            />
          </div>
        ) : (
          <p className={styles.text}>{toFixed(allocatedHours)}</p>
        )}
      </div>
      <div className={styles.cell}>
        <p className={styles.text}>{toFixed(spentTime)}</p>
      </div>
      <div className={styles.cell}>
        <p className={styles.text}>{toFixed(spentTimeInWeek)}</p>
      </div>
      <div className={styles.cell}>
        <p className={styles.text}>{toFixed(remainingTime)}</p>
      </div>
      {!!showCompletionColumn &&
        (progression || progression === 0 ? (
          <div className={styles.cell}>
            {type === "task" && canEdit ? (
              <div className={styles.numberInput}>
                <NumberInput
                  nbr={nextAllocatedHours}
                  max={100}
                  edit={(nbr) => nbr >= 0 && setNextAllocatedHours(nbr)}
                  submit={(percentComplete) => {
                    editAllocation({
                      allocation: {
                        ...allocation,
                        uuid: task?.allocations[0]?.uuid ?? "",
                      },
                      nextAllocation: {
                        ...allocation,
                        resource,
                        allocatedHours,
                        percentComplete,
                      },
                    });
                  }}
                />
                <p className={styles.percentSign}>%</p>
              </div>
            ) : (
              <p className={styles.text}>
                <span className={styles.progression}>
                  {toFixed(progression)}
                </span>
                %
              </p>
            )}
          </div>
        ) : (
          <div className={styles.cell}>
            <p className={styles.text}></p>
          </div>
        ))}
    </div>
  );
}

export { ResourceTimeOverview };
